/**
 * The typed error every API failure becomes (spec §11.1, §13).
 *
 * The API's error envelope is `{ error: { message, details? } }`
 * (apps/api/src/app.ts), where `details` is `{ field, message }[]` produced by
 * the shared zod schemas in `packages/types` (apps/api/src/middleware/validate.ts).
 * That `details` array is the reason this class exists: React Hook Form can
 * only show an error next to the input that caused it if something carries the
 * FIELD NAME across the boundary. A thrown `Error("Validation failed")` loses
 * it, and the screen ends up showing a banner that says "check your entries" —
 * exactly the kind of message §11.1 forbids.
 *
 * Split into its own module (rather than living in `api.ts`) so that tests, and
 * later `packages/ui`, can import the error shape without pulling in `env.ts`
 * and therefore without needing `VITE_API_BASE_URL` to be set.
 */

/** One field-level problem. Mirrors `ValidationErrorDetail` in apps/api. */
export interface ApiErrorDetail {
  /**
   * Dot-path of the offending field: `phone`, `lines.0.ratePaise`. Query and
   * param problems arrive prefixed (`query.q`, `params.id`) so a form can tell
   * a field error from a routing one — see apps/api/src/middleware/validate.ts.
   */
  field: string;
  message: string;
}

/**
 * `status` values that are not HTTP statuses.
 *
 * `0` means the request never reached the server. §11.1 requires "errors in
 * plain language ('No internet — saved as draft, will send when online')", and
 * that message is only correct for a transport failure — showing it for a 500
 * would tell the owner to wait for a network that is already working.
 */
export const NETWORK_FAILURE_STATUS = 0;

export class ApiError extends Error {
  /** HTTP status, or {@link NETWORK_FAILURE_STATUS} when the request never landed. */
  readonly status: number;
  /** Field-level validation problems. Empty for every non-400 error. */
  readonly details: readonly ApiErrorDetail[];
  /** Method and path, for logging. Never shown to the owner. */
  readonly endpoint: string;

  constructor(
    status: number,
    message: string,
    options: { details?: readonly ApiErrorDetail[]; endpoint?: string; cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = "ApiError";
    this.status = status;
    this.details = options.details ?? [];
    this.endpoint = options.endpoint ?? "";
  }

  /** The request never reached the server — offline, DNS, TLS, aborted. */
  get isNetworkFailure(): boolean {
    return this.status === NETWORK_FAILURE_STATUS;
  }

  /** A 400 carrying per-field detail. */
  get isValidation(): boolean {
    return this.status === 400 && this.details.length > 0;
  }

  /**
   * Not signed in, and the silent refresh could not fix it. `lib/api.ts` has
   * already cleared the session and started the redirect to the login screen by
   * the time a caller sees this.
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Signed in, but the capability is not held (§2.1). A screen should not be
   * able to produce one of these: `useCan()` reads the same map the server
   * does, so a 403 means the UI offered an action it should have hidden.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Either the document does not exist, or the FEATURE FLAG covering it is off
   * (§18.2 — "a disabled feature returns 404, not 403"). The client cannot and
   * must not try to distinguish those; telling them apart is precisely what the
   * 404 exists to prevent.
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Rate limited (apps/api/src/middleware/rate-limit.ts). */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /**
   * The server answered 5xx.
   *
   * IT NO LONGER MEANS "the message is unusable". Since 2026-08-20 `api.ts`
   * lets a DELIBERATE 5xx sentence through — the API flattens every crash to a
   * bare status line first, so anything else on a 5xx was written by a developer
   * for the owner to read, and "PUBLIC_APP_BASE_URL is not set on this server"
   * is worth vastly more than "Something went wrong". A caller that swaps
   * `message` for a generic sentence on this predicate is discarding the
   * diagnosis a second time; `message` is already safe to render.
   */
  get isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * `details` as a `{ field: message }` map, for
   * `Object.entries(...).forEach(([field, message]) => setError(field, ...))`.
   *
   * The FIRST message for a field wins: zod can report several problems for one
   * input, and a stack of three messages under a single field is noise. Fields
   * prefixed `query.` / `params.` are dropped — they are not form inputs, and
   * calling `setError("params.id")` on a form that has no such field silently
   * blocks submission with no visible error.
   */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.field.startsWith("query.") || detail.field.startsWith("params.")) continue;
      if (out[detail.field] === undefined) out[detail.field] = detail.message;
    }
    return out;
  }
}

/** Narrowing helper for `catch (error: unknown)`. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
